import { Button, Col, Form, Modal, Row } from 'react-angle-dashboard-components'

import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import './CustomPlanFormModal.css'
import { InvalidFeedback } from '../../style'
import InputWithTypeSelector from './InputWithTypeSelector'
import { useEffect } from 'react'
import { forbiddenPlanNameWords } from './forbiddenPlanNameWords'

const TRADITIONAL = 'traditional'
const HSA = 'HSA'

const COPAYMENT = 'copayment'
const COINSURANCE = 'coinsurance'

const sameTypeAndlessThan = <
  T extends { type: typeof COPAYMENT | typeof COINSURANCE; value: any }
>(
  a: T,
  b: T
) => {
  return a.type === b.type && a.value < b.value
}

const rangeNumberSchema = (
  min: number,
  max: number,
  increment = 10,
  unit = '$',
  unitAfter = false
) => {
  let errorText
  if (unitAfter) {
    errorText = `Please input a desired value from ${min}${unit}-${max}${unit}`
  } else {
    errorText = `Please input a desired value from ${unit}${min}-${unit}${max}`
  }
  return z
    .number({
      coerce: true
    })
    .min(min, errorText)
    .max(max, errorText)
    .int()
    .multipleOf(increment, `Must be a multiple of ${increment}`)
}

const serviceschema = ({
  minDollar,
  maxDollar,
  minPercent,
  maxPercent
}: {
  minDollar: number
  maxDollar: number
  minPercent: number
  maxPercent: number
}) =>
  z.discriminatedUnion('type', [
    z.object({
      type: z.literal(COPAYMENT), // $
      value: rangeNumberSchema(minDollar, maxDollar, 1, '$')
    }),
    z.object({
      type: z.literal(COINSURANCE), // %
      value: rangeNumberSchema(minPercent, maxPercent, 5, '%', true)
    })
  ])

const customPlanBaseSchema = () =>
  z.object({
    planName: z
      .string()
      .min(1, 'Plan name is required')
      .refine(
        (val) => /^[A-Za-z0-9 ]*$/.test(val),
        'Only letters and numbers are allowed'
      )
      .refine(
        (val) =>
          !forbiddenPlanNameWords.some((forbiddenWord) =>
            val.toLowerCase().includes(forbiddenWord)
          ),
        (val) => {
          const forbiddenWordInText = forbiddenPlanNameWords.find((forbiddenWord) => val.toLowerCase().includes(forbiddenWord))
          if (!forbiddenWordInText) {
            return {
              message: `You cannot use "${val}" in the plan name.`
            }
          }
          // Get the word in the same casing as it was entered
          const index = val.toLowerCase().indexOf(forbiddenWordInText)
          const word = val.substring(index, index + forbiddenWordInText.length)
          return {
            message: `You cannot use "${word}" in the plan name.`
          }
        }
      ),
    services: z.object({
      default: serviceschema({
        minDollar: 0,
        maxDollar: 9100,
        minPercent: 0,
        maxPercent: 30
      }),
      primaryCare: serviceschema({
        minDollar: 0,
        maxDollar: 500,
        minPercent: 0,
        maxPercent: 30
      }),
      specialist: serviceschema({
        minDollar: 0,
        maxDollar: 500,
        minPercent: 0,
        maxPercent: 30
      }),
      inPatient: serviceschema({
        minDollar: 0,
        maxDollar: 9100,
        minPercent: 0,
        maxPercent: 30
      }),
      rxTier1: serviceschema({
        minDollar: 0,
        maxDollar: 50,
        minPercent: 0,
        maxPercent: 30
      }),
      rxTier2: serviceschema({
        minDollar: 0,
        maxDollar: 500,
        minPercent: 0,
        maxPercent: 30
      }),
      rxTier3: serviceschema({
        minDollar: 0,
        maxDollar: 500,
        minPercent: 0,
        maxPercent: 30
      }),
      rxTier4: serviceschema({
        minDollar: 0,
        maxDollar: 9100,
        minPercent: 0,
        maxPercent: 30
      }),
      diagnostic: serviceschema({
        minDollar: 0,
        maxDollar: 500,
        minPercent: 0,
        maxPercent: 30
      }),
      urgentCare: serviceschema({
        minDollar: 0,
        maxDollar: 500,
        minPercent: 0,
        maxPercent: 30
      }),
      emergencyRoom: serviceschema({
        minDollar: 0,
        maxDollar: 9100,
        minPercent: 0,
        maxPercent: 30
      })
    })
  })

const customPlanSchema = z
  .discriminatedUnion(
    'type',
    [
      z
        .object({
          type: z.literal(HSA),
          innDeductible: rangeNumberSchema(1600, 8050),
          oopDeductible: rangeNumberSchema(1600, 8050)
        })
        .merge(customPlanBaseSchema()),
      z
        .object({
          type: z.literal(TRADITIONAL),
          innDeductible: rangeNumberSchema(0, 9450),
          oopDeductible: rangeNumberSchema(0, 9450)
        })
        .merge(customPlanBaseSchema())
    ],
    {
      errorMap: (issue) => {
        switch (issue.code) {
          case 'invalid_union_discriminator':
            return { message: 'A type is required' }
          default:
            return { message: 'Type is invalid' }
        }
      }
    }
  )
  .refine((schema) => schema.innDeductible <= schema.oopDeductible, {
    message: 'OOP Max cannot be less than deductible',
    path: ['oopDeductible']
  })
  .superRefine((val, ctx) => {
    const services = val.services

    if (sameTypeAndlessThan(services.specialist, services.primaryCare)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['services.specialist.value'],
        message: 'Specialist cannot be less than primary care'
      })
    }
    if (sameTypeAndlessThan(services.urgentCare, services.primaryCare)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['services.urgentCare.value'],
        message: 'Urgent care cannot be less than primary care'
      })
    }
    if (sameTypeAndlessThan(services.default, services.urgentCare)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['services.default.value'],
        message: 'Default cannot be less than urgent care'
      })
    }
    if (sameTypeAndlessThan(services.inPatient, services.urgentCare)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['services.inPatient.value'],
        message: 'In-Patient cannot be less than urgent care'
      })
    }
    if (sameTypeAndlessThan(services.emergencyRoom, services.urgentCare)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['services.emergencyRoom.value'],
        message: 'Emergency room cannot be less than urgent care'
      })
    }
    if (sameTypeAndlessThan(services.rxTier2, services.rxTier1)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['services.rxTier2.value'],
        message: 'Rx Tier 2 cannot be less than Rx Tier 1'
      })
    }
    if (sameTypeAndlessThan(services.rxTier3, services.rxTier2)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['services.rxTier3.value'],
        message: 'Rx Tier 3 cannot be less than Rx Tier 2'
      })
    }
    if (sameTypeAndlessThan(services.rxTier4, services.rxTier2)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['services.rxTier4.value'],
        message: 'Rx Tier 4 cannot be less than Rx Tier 2'
      })
    }
  })

const defaultInnCost = {
  value: 0,
  type: COPAYMENT as 'copayment'
}

export type ICustomPlanForm = z.infer<typeof customPlanSchema>

interface CustomPlanFormModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (formValues: ICustomPlanForm) => void
  formValues?: ICustomPlanForm
}

const CustomPlanFormModal = ({
  open,
  onClose,
  onSubmit,
  formValues
}: CustomPlanFormModalProps) => {
  const { register, control, handleSubmit, formState, trigger, reset } =
    useForm<ICustomPlanForm>({
      mode: 'all',
      resolver: zodResolver(customPlanSchema),
      defaultValues: {
        innDeductible: 0,
        oopDeductible: 0,
        services: {
          default: defaultInnCost,
          primaryCare: defaultInnCost,
          specialist: defaultInnCost,
          inPatient: defaultInnCost,
          rxTier1: defaultInnCost,
          rxTier2: defaultInnCost,
          rxTier3: defaultInnCost,
          rxTier4: defaultInnCost,
          diagnostic: defaultInnCost,
          urgentCare: defaultInnCost,
          emergencyRoom: defaultInnCost
        }
      }
    })

  const errors = formState.errors

  useEffect(() => {
    if (formValues) {
      reset(formValues)
    }
  }, [formValues, reset])

  return (
    <Modal
      show={open}
      centered
      onHide={onClose}
      dialogClassName="customPlanFormModalDialog"
      className="customPlanFormModal"
      style={{
        zIndex: 2000
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Add a custom plan</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Plan Name</Form.Label>
              <Form.Control
                {...register('planName')}
                isInvalid={!!errors.planName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.planName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Type</Form.Label>
              <Form.Check>
                <Form.Check.Input
                  type="radio"
                  value={TRADITIONAL}
                  {...register('type')}
                  isInvalid={!!errors.type}
                />
                <Form.Check.Label>Traditional</Form.Check.Label>
              </Form.Check>
              <Form.Check>
                <Form.Check.Input
                  type="radio"
                  value={HSA}
                  {...register('type')}
                  id="type"
                  isInvalid={!!errors.type}
                />
                <Form.Check.Label>HDHP (HSA Eligible)</Form.Check.Label>
              </Form.Check>
              {!!errors.type && (
                <InvalidFeedback>{errors.type?.message}</InvalidFeedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Deductible INN</Form.Label>
              <Form.Control
                {...register('innDeductible')}
                isInvalid={!!errors.innDeductible}
              />
              <Form.Control.Feedback type="invalid">
                {errors.innDeductible?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Deductible OOP</Form.Label>
              <Form.Control
                {...register('oopDeductible')}
                isInvalid={!!errors.oopDeductible}
              />
              <Form.Control.Feedback type="invalid">
                {errors.oopDeductible?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <hr />
        <p>Costshare for In-Network Services:</p>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Default</Form.Label>
              <InputWithTypeSelector
                register={register}
                control={control}
                fieldKey="services.default"
                isInvalid={!!errors.services?.default}
                trigger={trigger}
              />
              {!!errors.services?.default && (
                <InvalidFeedback>
                  {errors.services?.default?.value?.message}
                </InvalidFeedback>
              )}
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Primary Care</Form.Label>
              <InputWithTypeSelector
                register={register}
                control={control}
                fieldKey="services.primaryCare"
                isInvalid={!!errors.services?.primaryCare}
                trigger={trigger}
              />
              {!!errors.services?.primaryCare && (
                <InvalidFeedback>
                  {errors.services?.primaryCare?.value?.message}
                </InvalidFeedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Specialist</Form.Label>
              <InputWithTypeSelector
                register={register}
                control={control}
                fieldKey="services.specialist"
                isInvalid={!!errors.services?.specialist}
                trigger={trigger}
              />
              {!!errors.services?.specialist && (
                <InvalidFeedback>
                  {errors.services?.specialist?.value?.message}
                </InvalidFeedback>
              )}
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>In Patient</Form.Label>
              <InputWithTypeSelector
                register={register}
                control={control}
                fieldKey="services.inPatient"
                isInvalid={!!errors.services?.inPatient}
                trigger={trigger}
              />
              {!!errors.services?.inPatient && (
                <InvalidFeedback>
                  {errors.services?.inPatient?.value?.message}
                </InvalidFeedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Pharmacy Tier 1</Form.Label>
              <InputWithTypeSelector
                register={register}
                control={control}
                fieldKey="services.rxTier1"
                isInvalid={!!errors.services?.rxTier1}
                trigger={trigger}
              />
              {!!errors.services?.rxTier1 && (
                <InvalidFeedback>
                  {errors.services?.rxTier1?.value?.message}
                </InvalidFeedback>
              )}
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Pharmacy Tier 2</Form.Label>
              <InputWithTypeSelector
                register={register}
                control={control}
                fieldKey="services.rxTier2"
                isInvalid={!!errors.services?.rxTier2}
                trigger={trigger}
              />
              {!!errors.services?.rxTier2 && (
                <InvalidFeedback>
                  {errors.services?.rxTier2?.value?.message}
                </InvalidFeedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Pharmacy Tier 3</Form.Label>
              <InputWithTypeSelector
                register={register}
                control={control}
                fieldKey="services.rxTier3"
                isInvalid={!!errors.services?.rxTier3}
                trigger={trigger}
              />
              {!!errors.services?.rxTier3 && (
                <InvalidFeedback>
                  {errors.services?.rxTier3?.value?.message}
                </InvalidFeedback>
              )}
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Pharmacy Tier 4</Form.Label>
              <InputWithTypeSelector
                register={register}
                control={control}
                fieldKey="services.rxTier4"
                isInvalid={!!errors.services?.rxTier4}
                trigger={trigger}
              />
              {!!errors.services?.rxTier4 && (
                <InvalidFeedback>
                  {errors.services?.rxTier4?.value?.message}
                </InvalidFeedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Diagnostic</Form.Label>
              <InputWithTypeSelector
                register={register}
                control={control}
                fieldKey="services.diagnostic"
                isInvalid={!!errors.services?.diagnostic}
                trigger={trigger}
              />
              {!!errors.services?.diagnostic && (
                <InvalidFeedback>
                  {errors.services?.diagnostic?.value?.message}
                </InvalidFeedback>
              )}
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>Urgent Care</Form.Label>
              <InputWithTypeSelector
                register={register}
                control={control}
                fieldKey="services.urgentCare"
                isInvalid={!!errors.services?.urgentCare}
                trigger={trigger}
              />
              {!!errors.services?.urgentCare && (
                <InvalidFeedback>
                  {errors.services?.urgentCare?.value?.message}
                </InvalidFeedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Emergency Room</Form.Label>
              <InputWithTypeSelector
                register={register}
                control={control}
                fieldKey="services.emergencyRoom"
                isInvalid={!!errors.services?.emergencyRoom}
                trigger={trigger}
              />
              {!!errors.services?.emergencyRoom && (
                <InvalidFeedback>
                  {errors.services?.emergencyRoom?.value?.message}
                </InvalidFeedback>
              )}
            </Form.Group>
          </Col>
          <Col></Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="outline-primary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit(onSubmit)}>Create</Button>
      </Modal.Footer>
    </Modal>
  )
}

export default CustomPlanFormModal
